import { useState } from "react";
import { Button, Textarea, Toast, Icon, type ToastType } from "@legal-500/legal500-design-system";
import type { ApprovalRequest } from "../../../../api/approvalRequests.service";
import { formatDateTime } from "@/utils";
import { mapStatus, getDisplayContent, canApproveOrReject, canSuspend } from "../utils";
import { useUnlock } from "../../../../hooks/useLock";
import { usePermissions } from "@/hooks/usePermissions";
import styles from "./ContentColumn.module.css";

type ContentColumnProps = {
  item: ApprovalRequest;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onSuspend: (id: string) => void;
  isEditing: boolean;
  onEditStart: (id: string) => void;
  onEditCancel: (id: string) => void;
  onSave: (id: string, contentOutput: string) => void;
  saving?: boolean;
  lockError: Error | null;
  locking?: boolean;
};

export function ContentColumn({
  item,
  onApprove,
  onReject,
  onSuspend,
  isEditing,
  onEditStart,
  onEditCancel,
  onSave,
  saving,
  lockError,
  locking = false,
}: ContentColumnProps) {
  const status = mapStatus(item.status);
  const canApproveReject = canApproveOrReject(status);
  const canSuspendRow = canSuspend(status);
  const canEdit = status === "NEW";
  const [editedContent, setEditedContent] = useState(getDisplayContent(item));
  const { mutate: unlock, loading: unlocking } = useUnlock();
  const { user } = usePermissions();
  const currentUserId = user?.id;
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const isLockedByOther = lockError
    ? (lockError as Error & { status?: number }).status === 409
    : false;

  const isLockedFromApi =
    item.lockedTo &&
    item.lockedBy &&
    new Date(item.lockedTo) > new Date() &&
    (!currentUserId || item.lockedBy !== currentUserId);

  const formatLockErrorMessage = (message: string): string => {
    const dateMatch = message.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)/);
    if (dateMatch) {
      const isoDate = dateMatch[1];
      const formattedDate = formatDateTime(isoDate);
      return message.replace(isoDate, formattedDate);
    }
    return message;
  };

  const getLockErrorMessage = (): string | null => {
    if (isLockedByOther && lockError) {
      return formatLockErrorMessage(lockError.message || "This record is locked by another user");
    }
    if (isLockedFromApi && item.lockedTo) {
      const formattedDate = formatDateTime(item.lockedTo);
      return `Record is already locked by another user until ${formattedDate}`;
    }
    return null;
  };

  const lockErrorMessage = getLockErrorMessage();
  const isActuallyLocked = isLockedByOther || isLockedFromApi;

  const handleCancel = async () => {
    try {
      await unlock(item.id);
    } catch (_error) {
      setToast({
        message: "Failed to unlock record. It will unlock automatically after some time.",
        type: "error",
      });
    }
    setEditedContent(getDisplayContent(item));
    onEditCancel(item.id);
  };

  const isBlank = editedContent.trim() === "";

  const handleSave = () => {
    if (isBlank) return;
    onSave(item.id, editedContent);
  };

  if (isEditing) {
    return (
      <div className={styles.contentColumn} onClick={e => e.stopPropagation()}>
        <div className={styles.editContainer} onClick={e => e.stopPropagation()}>
          <Textarea
            value={editedContent}
            onChange={e => setEditedContent(e.target.value)}
            onClick={e => e.stopPropagation()}
            className={styles.editTextarea}
            rows={6}
          />
          <div className={styles.editActions} onClick={e => e.stopPropagation()}>
            <Button
              variant="outlined"
              color="default"
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleCancel();
              }}
              disabled={unlocking}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={saving || isBlank}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentColumn}>
      <div className={styles.contentText}>{getDisplayContent(item)}</div>
      <div className={styles.contentActions} onClick={e => e.stopPropagation()}>
        {canApproveReject && !isActuallyLocked && (
          <>
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={e => {
                e.stopPropagation();
                onApprove(item.id);
              }}
              className={styles.approveButton}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={e => {
                e.stopPropagation();
                onReject(item.id);
              }}
              className={styles.rejectButton}
            >
              Reject
            </Button>
          </>
        )}
        {canSuspendRow && !isActuallyLocked && (
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={e => {
              e.stopPropagation();
              onSuspend(item.id);
            }}
          >
            Suspend
          </Button>
        )}
        {canEdit && !isActuallyLocked && !isEditing && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                aria-hidden="true"
                style={{ fill: "currentColor" }}
              >
                <path d="M21 6 18 3l-2 2 3 3 2-2Zm-3 3-3-3L4 17l-1 4 4-1L18 9Z" />
              </svg>
            }
            onClick={e => {
              e.stopPropagation();
              onEditStart(item.id);
            }}
            disabled={locking}
            className={styles.editButton}
          >
            Edit
          </Button>
        )}
      </div>
      {lockErrorMessage && (
        <div className={styles.lockError} onClick={e => e.stopPropagation()}>
          {lockErrorMessage}
        </div>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}
